import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Row, Col, Typography, Tag, Button, Rate, Divider, Spin, Avatar, Card,
  Form, Input, message, Empty, List, Space, Progress, Tooltip, Alert,
} from 'antd';
import {
  HeartOutlined, HeartFilled, LinkOutlined, StarOutlined,
  EyeOutlined, ArrowLeftOutlined, UserOutlined, FileTextOutlined,
  SafetyCertificateOutlined, GlobalOutlined, CheckCircleOutlined,
  CloseCircleOutlined, SyncOutlined, MinusCircleOutlined,
  InfoCircleOutlined, EditOutlined,
} from '@ant-design/icons';
import { AuthContext } from '../store/AuthContext';
import { toolsAPI, reviewsAPI } from '../services/api';
import { Tool, Review, ComplianceStatus } from '../types';
import { useLanguage } from '../store/LanguageContext';
import DescriptionRenderer from '../components/DescriptionRenderer';
import {
  availabilityCNLabels,
  availabilityCNFullLabels,
  availabilityCNTooltips,
  availabilityCNTagColor,
  availabilityCNBadge,
} from '../constants/availabilityCN';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-cn';
import { getToolDescriptionByLanguage, getToolNameByLanguage, getToolShortDescriptionByLanguage } from '../utils/toolLocale';
import { getCategoryLabel } from '../utils/categoryLocale';
import { getToolLogoUrl, getToolShowcaseUrl } from '../utils/toolAssets';
import { usePageSeo } from '../utils/usePageSeo';
import { toolHasVerifiedStats } from '../utils/toolStats';
import { getDisplayTags } from '../utils/tagLocale';
import { AdSlot } from '../components/AdsBootstrap';

dayjs.extend(relativeTime);

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

const pricingMapBase: Record<string, { color: string; key: string }> = {
  free: { color: 'green', key: 'pricing.free' },
  freemium: { color: 'blue', key: 'pricing.freemium' },
  paid: { color: 'orange', key: 'pricing.paid' },
  contact: { color: 'purple', key: 'pricing.contact' },
};

const sourceSiteMapBase: Record<string, string> = {
  aicpb: 'source.aicpb',
  topai: 'source.topai',
  manual: 'source.manual',
  custom: 'source.custom',
};

/* 可用性映射：引用集中常量 */
const availabilityMap: Record<string, { color: string; label: string; emoji: string }> = {
  cn_native: { color: availabilityCNTagColor.cn_native, label: availabilityCNLabels.cn_native, emoji: availabilityCNBadge.cn_native.emoji },
  direct:    { color: availabilityCNTagColor.direct,    label: availabilityCNLabels.direct,    emoji: availabilityCNBadge.direct.emoji },
  mirror:    { color: availabilityCNTagColor.mirror,    label: availabilityCNLabels.mirror,    emoji: availabilityCNBadge.mirror.emoji },
  vpn:       { color: availabilityCNTagColor.vpn,       label: availabilityCNLabels.vpn,       emoji: availabilityCNBadge.vpn.emoji },
  blocked:   { color: availabilityCNTagColor.blocked,   label: availabilityCNLabels.blocked,   emoji: availabilityCNBadge.blocked.emoji },
  unknown:   { color: availabilityCNTagColor.unknown,   label: availabilityCNLabels.unknown,   emoji: availabilityCNBadge.unknown.emoji },
};

const statusMetaBase: Record<ComplianceStatus, { icon: React.ReactNode; color: string; key: string }> = {
  passed: { icon: <CheckCircleOutlined />, color: '#52c41a', key: 'status.passed' },
  failed: { icon: <CloseCircleOutlined />, color: '#ff4d4f', key: 'status.failed' },
  in_progress: { icon: <SyncOutlined spin />, color: '#1890ff', key: 'status.in_progress' },
  not_started: { icon: <MinusCircleOutlined />, color: '#d9d9d9', key: 'status.not_started' },
};

const dimensionKeys: { key: string; labelKey: string }[] = [
  { key: 'data_residency', labelKey: 'dim.data_residency' },
  { key: 'privacy', labelKey: 'dim.privacy' },
  { key: 'encryption', labelKey: 'dim.encryption' },
  { key: 'certification', labelKey: 'dim.certification' },
  { key: 'content_moderation', labelKey: 'dim.content_moderation' },
  { key: 'audit_logging', labelKey: 'dim.audit_logging' },
  { key: 'sla_support', labelKey: 'dim.sla_support' },
  { key: 'export_control', labelKey: 'dim.export_control' },
];

const ToolDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { t, language } = useLanguage();

  // Set dayjs locale dynamically
  dayjs.locale(language === 'zh-CN' ? 'zh-cn' : 'en');

  const [tool, setTool] = useState<Tool | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isFavorited, setIsFavorited] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingReviewId, setEditingReviewId] = useState<number | null>(null);

  const [form] = Form.useForm();

  const toolName = tool ? getToolNameByLanguage(tool, language) : '';
  const toolShort = tool
    ? (getToolShortDescriptionByLanguage(tool, language)
      || getToolDescriptionByLanguage(tool, language)
      || '').replace(/\s+/g, ' ').trim()
    : '';
  const toolImage = tool ? (getToolLogoUrl(tool) || getToolShowcaseUrl(tool) || undefined) : undefined;

  const toolJsonLd = useMemo(() => {
    if (!tool) return null;
    const origin = typeof window !== 'undefined'
      ? `${window.location.protocol}//${window.location.host}`
      : 'https://aihomehub.cn';
    const data: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: toolName,
      description: toolShort.slice(0, 300),
      url: `${origin}/tools/${tool.slug}`,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: language === 'zh-CN' ? 'CNY' : 'USD',
      },
    };
    if (toolImage) data.image = toolImage;
    if (tool.website_url) data.sameAs = tool.website_url;
    if (tool.rating > 0 && tool.review_count > 0) {
      data.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: Number(tool.rating).toFixed(1),
        ratingCount: tool.review_count,
        bestRating: '5',
        worstRating: '1',
      };
    }
    return data;
  }, [tool, toolName, toolShort, toolImage, language]);

  usePageSeo({
    title: tool
      ? (language === 'zh-CN'
        ? `${toolName} - AI工具介绍与评测`
        : `${toolName} - AI Tool Review`)
      : undefined,
    description: tool
      ? (toolShort.slice(0, 160)
        || (language === 'zh-CN'
          ? `${toolName} 是什么？功能介绍、定价与使用评测。`
          : `What is ${toolName}? Features, pricing, and reviews.`))
      : undefined,
    path: tool ? `/tools/${tool.slug}` : undefined,
    image: toolImage,
    jsonLd: toolJsonLd,
  });

  const fetchReviews = useCallback(async () => {
    if (!slug) return;
    try {
      setReviewLoading(true);
      const toolRes = await toolsAPI.getToolBySlug(slug!);
      const toolId = toolRes.data.tool.id;
      const res = await reviewsAPI.getByTool(toolId);
      setReviews(res.data.reviews || []);
    } catch (error) {
      console.error('获取评论失败:', error);
    } finally {
      setReviewLoading(false);
    }
  }, [slug]);

  const fetchTool = useCallback(async () => {
    try {
      setLoading(true);
      const res = await toolsAPI.getToolBySlug(slug!);
      setTool(res.data.tool);
      fetchReviews();
    } catch (error) {
      message.error(t('detail.notFound'));
      navigate('/tools');
    } finally {
      setLoading(false);
    }
  }, [slug, fetchReviews, navigate]);

  useEffect(() => {
    if (slug) {
      fetchTool();
    }
  }, [slug, fetchTool]);

  const handleVisitWebsite = () => {
    if (!tool) return;
    toolsAPI.trackClick(tool.id).catch(() => {});
    if (tool.website_url) window.open(tool.website_url, '_blank', 'noopener,noreferrer');
  };

  const handleFavorite = async () => {
    if (!user) {
      message.info(t('common.pleaseLogin'));
      navigate('/login');
      return;
    }
    if (!tool) return;
    try {
      await toolsAPI.toggleFavorite(tool.id);
      setIsFavorited(!isFavorited);
      message.success(isFavorited ? t('detail.unfavorited') : t('detail.favoriteSuccess'));
    } catch (error) {
      message.error(t('common.operationFailed'));
    }
  };

  const handleSubmitReview = async (values: { rating: number; title: string; comment: string }) => {
    if (!user) {
      message.info(t('common.pleaseLogin'));
      navigate('/login');
      return;
    }
    if (!tool) return;
    try {
      setSubmitting(true);
      if (editingReviewId) {
        await reviewsAPI.update(editingReviewId, values);
        message.success(t('detail.reviewUpdated'));
      } else {
        await reviewsAPI.create({ tool_id: tool.id, ...values });
        message.success(t('detail.reviewSubmitted'));
      }
      form.resetFields();
      setEditingReviewId(null);
      fetchReviews();
      fetchTool(); // 刷新评分
    } catch (error: any) {
      message.error(error.response?.data?.message || t('common.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" tip={t('common.loading')} />
      </div>
    );
  }

  if (!tool) {
    return <Empty description={t('detail.toolNotExist')} />;
  }

  const localizedName = getToolNameByLanguage(tool, language);
  const localizedDescription = getToolDescriptionByLanguage(tool, language);
  const localizedShortDescription = getToolShortDescriptionByLanguage(tool, language);

  const pricingBase = pricingMapBase[tool.pricing_type] || pricingMapBase.contact;
  const tags = getDisplayTags(tool.tags, language, 8);
  const sourceLabelKey = sourceSiteMapBase[tool.source_site];
  const sourceLabel = sourceLabelKey ? t(sourceLabelKey) : tool.source_site;

  return (
    <div className="page-container" style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 16px' }}>
      {/* 返回按钮 */}
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        style={{ marginBottom: 16 }}
      >
        {t('common.back')}
      </Button>

      {/* 工具头部 */}
      <div className="tool-detail-header" style={{ background: '#fff', borderRadius: 16, padding: '32px 32px 28px', marginBottom: 24 }}>
        <Row gutter={[32, 24]} align="top">
          {/* 左侧：工具信息 */}
          <Col xs={24} md={13} lg={14}>
            {/* Logo + 标题行 */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
              <Avatar
                src={getToolLogoUrl(tool)}
                size={72}
                shape="square"
                style={{ borderRadius: 14, background: '#1677ff', fontSize: 28, flexShrink: 0 }}
              >
                {localizedName.charAt(0)}
              </Avatar>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                  <Title level={2} style={{ margin: 0, fontSize: 28, lineHeight: 1.25 }}>{localizedName}</Title>
                  {tool.is_featured && <Tag color="gold" style={{ borderRadius: 20 }}>{t('detail.featured')}</Tag>}
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => navigate(`/feedback?toolId=${tool.id}`)}
                    style={{ fontSize: 12, padding: 0 }}
                  >
                    {t('detail.correctFeedback')}
                  </Button>
                </div>
                <Space size={[6, 6]} wrap>
                  <Tag color="processing" style={{ borderRadius: 20 }}>{tool.category ? getCategoryLabel(tool.category, language) : t('detail.uncategorized')}</Tag>
                  <Tag color={pricingBase.color} style={{ borderRadius: 20 }}>{t(pricingBase.key)}</Tag>
                  {tool.availability_cn && tool.availability_cn !== 'unknown' && (
                    <Tag style={{ borderRadius: 20 }}>
                      {availabilityMap[tool.availability_cn]?.emoji} {availabilityMap[tool.availability_cn]?.label}
                    </Tag>
                  )}
                </Space>
              </div>
            </div>

            {/* 描述 */}
            <Paragraph type="secondary" style={{ margin: '0 0 14px', fontSize: 15, lineHeight: 1.7 }}>
              {localizedShortDescription || localizedDescription}
            </Paragraph>

            {/* Hashtag 标签 */}
            {tags.length > 0 && (
              <Space size={[6, 6]} wrap style={{ marginBottom: 16 }}>
                {tags.slice(0, 8).map((tag: string, index: number) => (
                  <Tag key={index} color="geekblue" style={{ borderRadius: 999, fontSize: 12 }}>
                    #{tag}
                  </Tag>
                ))}
              </Space>
            )}

            {/* 评分 + 浏览（仅真实用户评价时展示） */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
              {toolHasVerifiedStats(tool) ? (
                <>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Rate disabled defaultValue={Number(tool.rating) || 0} allowHalf style={{ fontSize: 14 }} />
                    <Text strong>{Number(tool.rating)?.toFixed(1) || '0.0'}</Text>
                    <Text type="secondary" style={{ fontSize: 13 }}>({t('detail.reviewCount', { count: String(tool.review_count) })})</Text>
                  </span>
                  {Number(tool.view_count) > 0 && (
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      <EyeOutlined style={{ marginRight: 4 }} />{t('detail.viewCount', { count: String(tool.view_count) })}
                    </Text>
                  )}
                </>
              ) : (
                <Text type="secondary" style={{ fontSize: 13 }}>{t('detail.noRating')}</Text>
              )}
              {tool.source_url && (
                <Tag style={{ margin: 0 }}>
                  <a href={tool.source_url} target="_blank" rel="noopener noreferrer">{t('detail.sourceEntry')}</a>
                </Tag>
              )}
            </div>

            {/* 操作按钮 */}
            <Space size={10} wrap>
              <Button
                type="primary"
                size="large"
                icon={<LinkOutlined />}
                onClick={handleVisitWebsite}
                style={{ borderRadius: 8 }}
              >
                {t('detail.visitWebsite')}
              </Button>
              <Button
                size="large"
                icon={isFavorited ? <HeartFilled style={{ color: '#ff4d4f' }} /> : <HeartOutlined />}
                onClick={handleFavorite}
                style={{ borderRadius: 8 }}
              >
                {isFavorited ? t('detail.favorited') : t('detail.favorite')}
              </Button>
            </Space>
          </Col>

          {/* 右侧：网站展示 */}
          <Col xs={24} md={11} lg={10}>
            {/* 浏览器边框容器 */}
            <div style={{
              borderRadius: 14,
              overflow: 'hidden',
              border: '1px solid #e5e7eb',
              boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
              background: '#fff',
            }}>
              {/* 浏览器顶栏 */}
              <div style={{
                background: '#f3f4f6',
                borderBottom: '1px solid #e5e7eb',
                padding: '9px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#ff5f57', display: 'inline-block' }} />
                <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#febc2e', display: 'inline-block' }} />
                <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#28c840', display: 'inline-block' }} />
                <div style={{
                  flex: 1,
                  marginLeft: 8,
                  background: '#fff',
                  borderRadius: 6,
                  border: '1px solid #e5e7eb',
                  padding: '3px 10px',
                  fontSize: 11,
                  color: '#9ca3af',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 240,
                }}>
                  {tool.website_url?.replace(/^https?:\/\//, '') || tool.name}
                </div>
                <a
                  href={tool.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#9ca3af', lineHeight: 1, marginLeft: 2 }}
                  onClick={e => e.stopPropagation()}
                >
                  <LinkOutlined style={{ fontSize: 12 }} />
                </a>
              </div>

              {/* 展示图 or 占位 */}
              {getToolShowcaseUrl(tool) ? (
                <img
                  src={getToolShowcaseUrl(tool) || undefined}
                  alt={t('detail.showcaseAlt', { name: localizedName })}
                  style={{ width: '100%', maxHeight: 320, objectFit: 'cover', display: 'block' }}
                  loading="lazy"
                />
              ) : (
                <div style={{
                  height: 260,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 14,
                  background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)',
                }}>
                  <Avatar
                    src={getToolLogoUrl(tool)}
                    size={64}
                    shape="square"
                    style={{ borderRadius: 14, background: '#16a34a', fontSize: 28, flexShrink: 0, boxShadow: '0 4px 16px rgba(22,163,74,0.25)' }}
                  >
                    {tool.category?.icon || tool.name.charAt(0)}
                  </Avatar>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', marginBottom: 4 }}>{localizedName}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', maxWidth: 200 }}>{t('detail.noScreenshot')}</div>
                  </div>
                  <a
                    href={tool.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '7px 18px',
                      background: '#16a34a',
                      color: '#fff',
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 600,
                      textDecoration: 'none',
                      boxShadow: '0 2px 8px rgba(22,163,74,0.3)',
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    <GlobalOutlined style={{ fontSize: 13 }} /> {t('detail.visitSite')}
                  </a>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>

      {/* 已移入头部右侧，此处不再重复展示图 */}

      <Row gutter={24}>
        {/* 左侧详情 */}
        <Col xs={24} md={16}>
          {/* 详细描述 */}
          <Card style={{ marginBottom: 24, borderRadius: 12 }}>
            <Title level={4}>
              <FileTextOutlined /> {t('detail.description')}
            </Title>
            <DescriptionRenderer description={localizedDescription} toolName={localizedName} />

            {tags.length > 0 && (
              <div style={{ marginTop: 16 }}>
                {tags.map((tag: string, index: number) => (
                  <Tag key={index} color="processing" style={{ marginBottom: 6, borderRadius: 999 }}>
                    #{tag}
                  </Tag>
                ))}
              </div>
            )}
          </Card>

          {/* 评论区 */}
          <Card style={{ borderRadius: 12 }}>
            <Title level={4}>
              <StarOutlined /> {t('detail.reviews')} ({reviews.length})
            </Title>

            {/* 评论表单 */}
            {user ? (
              <Form form={form} onFinish={handleSubmitReview} layout="vertical" style={{ marginBottom: 24 }}>
                <Form.Item name="rating" label={t('detail.ratingLabel')} rules={[{ required: true, message: t('detail.ratingRequired') }]}>
                  <Rate />
                </Form.Item>
                <Form.Item name="title" label={t('detail.reviewTitleLabel')} rules={[{ required: true, message: t('detail.reviewTitleRequired') }]}>
                  <Input placeholder={t('detail.reviewTitlePlaceholder')} maxLength={100} />
                </Form.Item>
                <Form.Item
                  name="comment"
                  label={t('detail.reviewContentLabel')}
                  rules={[
                    { required: true, message: t('detail.reviewContentRequired') },
                    { min: 10, message: t('detail.reviewContentMin') },
                  ]}
                >
                  <TextArea rows={4} placeholder={t('detail.reviewContentPlaceholder')} maxLength={2000} showCount />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={submitting}>
                    {editingReviewId ? t('detail.updateReview') : t('detail.submitReview')}
                  </Button>
                  {editingReviewId && (
                    <Button
                      style={{ marginLeft: 8 }}
                      onClick={() => {
                        setEditingReviewId(null);
                        form.resetFields();
                      }}
                    >
                      {t('detail.cancelEdit')}
                    </Button>
                  )}
                </Form.Item>
              </Form>
            ) : (
              <div style={{ textAlign: 'center', padding: '16px 0', marginBottom: 16, background: '#fafafa', borderRadius: 8 }}>
                <Text type="secondary">
                  <Button type="link" onClick={() => navigate('/login')}>{t('nav.login')}</Button> {t('detail.loginToReview')}
                </Text>
              </div>
            )}

            <Divider />

            {/* 评论列表 */}
            {reviewLoading ? (
              <Spin />
            ) : reviews.length > 0 ? (
              <List
                dataSource={reviews}
                renderItem={(review) => (
                  <List.Item style={{ borderBottom: '1px solid #f0f0f0', padding: '16px 0' }}>
                    <List.Item.Meta
                      avatar={
                        <Avatar icon={<UserOutlined />}>
                          {review.user?.username?.charAt(0) || 'U'}
                        </Avatar>
                      }
                      title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Space>
                            <Text strong>{review.user?.username || t('detail.anonymousUser')}</Text>
                            <Rate disabled defaultValue={review.rating} style={{ fontSize: 14 }} />
                          </Space>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {dayjs(review.created_at).fromNow()}
                          </Text>
                        </div>
                      }
                      description={
                        <>
                          {review.title && <Text strong>{review.title}</Text>}
                          <Paragraph style={{ margin: '4px 0 0' }}>{review.comment}</Paragraph>
                          {user && (review.user_id === user.id || user.role === 'admin') && (
                            <Button
                              type="link"
                              size="small"
                              style={{ paddingLeft: 0 }}
                              onClick={() => {
                                setEditingReviewId(review.id);
                                form.setFieldsValue({
                                  rating: review.rating,
                                  title: review.title,
                                  comment: review.comment,
                                });
                              }}
                            >
                              {t('detail.editReview')}
                            </Button>
                          )}
                        </>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty description={t('detail.noReviews')} />
            )}
          </Card>
        </Col>

        {/* 右侧信息 */}
        <Col xs={24} md={8}>
          <Card style={{ borderRadius: 12, marginBottom: 16 }}>
            <Title level={5}>{t('detail.productInfo')}</Title>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <Text type="secondary">{t('detail.categoryLabel')}</Text>
                <Tag color="blue">{tool.category ? getCategoryLabel(tool.category, language) : t('detail.uncategorized')}</Tag>
              </div>
              <div>
                <Text type="secondary">{t('detail.sourceLabel')}</Text>
                <Text>{sourceLabel}</Text>
              </div>
              <div>
                <Text type="secondary">{t('detail.pricingLabel')}</Text>
                <Tag color={pricingBase.color}>{t(pricingBase.key)}</Tag>
              </div>
              {tool.pricing_detail && (
                <div>
                  <Text type="secondary">{t('detail.pricingDetail')}</Text>
                  <Text>{tool.pricing_detail}</Text>
                </div>
              )}
              <div>
                <Text type="secondary">{t('detail.ratingInfoLabel')}</Text>
                <Text strong>{Number(tool.rating)?.toFixed(1) || '0.0'} / 5.0</Text>
              </div>
              <div>
                <Text type="secondary">{t('detail.reviewCountLabel')}</Text>
                <Text>{tool.review_count}{t('detail.reviewCountSuffix')}</Text>
              </div>
              <div>
                <Text type="secondary">{t('detail.viewCountLabel')}</Text>
                <Text>{tool.view_count}{t('detail.viewCountSuffix')}</Text>
              </div>
            </div>
          </Card>

          {/* 中国可用性：无数据时不展示空卡片 */}
          {(tool.availability_cn || tool.cn_url) && (
          <Card style={{ borderRadius: 12, marginBottom: 16 }}>
            <Title level={5}><GlobalOutlined /> {t('detail.chinaAvailability')}</Title>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {tool.availability_cn && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Tag
                    color={availabilityMap[tool.availability_cn]?.color || '#d9d9d9'}
                    style={{ fontSize: 14, padding: '4px 12px', width: 'fit-content' }}
                  >
                    {availabilityMap[tool.availability_cn]?.emoji} {availabilityCNFullLabels[tool.availability_cn] || availabilityMap[tool.availability_cn]?.label}
                  </Tag>
                  <Tooltip
                    title={availabilityCNTooltips[tool.availability_cn]}
                    overlayStyle={{ maxWidth: 360 }}
                  >
                    <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 16, cursor: 'pointer' }} />
                  </Tooltip>
                </div>
              )}
              {/* 合规提示卡片 */}
              {tool.availability_cn && tool.availability_cn !== 'direct' && tool.availability_cn !== 'cn_native' && (
                <Alert
                  type={tool.availability_cn === 'blocked' ? 'error' : tool.availability_cn === 'vpn' ? 'warning' : 'info'}
                  showIcon
                  message={availabilityCNTooltips[tool.availability_cn] || ''}
                  style={{ fontSize: 12, borderRadius: 8 }}
                />
              )}
              {tool.cn_url && (
                <div>
                  <Text type="secondary">{t('detail.cnUrl')}: </Text>
                  <a href={tool.cn_url} target="_blank" rel="noopener noreferrer">{tool.cn_url}</a>
                </div>
              )}
              {tool.cn_icp && (
                <div><Text type="secondary">{t('detail.cnIcp')}: </Text><Text>{tool.cn_icp}</Text></div>
              )}
              {tool.cn_app_store && (
                <Tag color="green" icon={<CheckCircleOutlined />}>{t('detail.cnAppStoreAvailable')}</Tag>
              )}
              {tool.cn_wechat_mini && (
                <Tag color="green" icon={<CheckCircleOutlined />}>{t('detail.cnWechatAvailable')}</Tag>
              )}
            </div>
          </Card>
          )}

          {/* 合规维度雷达 */}
          {tool.compliance_tier && tool.compliance_tier !== 'pending' && (
            <Card style={{ borderRadius: 12, marginBottom: 16 }}>
              <Title level={5}>
                <SafetyCertificateOutlined /> {t('detail.complianceScoreTitle')}
                <Tag
                  color={tool.compliance_tier === 'T1_full' ? 'green' : tool.compliance_tier === 'T2_partial' ? 'blue' : 'default'}
                  style={{ marginLeft: 8 }}
                >
                  {tool.compliance_tier === 'T1_full' ? t('tier.T1_full') : tool.compliance_tier === 'T2_partial' ? t('tier.T2_partial') : t('tier.T3_none')}
                </Tag>
              </Title>
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <Progress
                  type="dashboard"
                  percent={tool.compliance_score || 0}
                  strokeColor={
                    (tool.compliance_score || 0) >= 80 ? '#52c41a' :
                    (tool.compliance_score || 0) >= 50 ? '#1890ff' : '#ff4d4f'
                  }
                  format={(p) => <span style={{ fontSize: 20, fontWeight: 600 }}>{p}</span>}
                  width={120}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {dimensionKeys.map(({ key, labelKey }) => {
                  const score = (tool as any)[`dim_${key}_score`] as number || 0;
                  const status = (tool as any)[`dim_${key}_status`] as ComplianceStatus || 'not_started';
                  const evidence = (tool as any)[`dim_${key}_evidence`] as string | undefined;
                  const meta = statusMetaBase[status];
                  return (
                    <div key={key}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <Text style={{ fontSize: 13 }}>{t(labelKey)}</Text>
                        <Tooltip title={evidence || t(meta.key)}>
                          <span style={{ color: meta.color, fontSize: 12 }}>{meta.icon} {t(meta.key)}</span>
                        </Tooltip>
                      </div>
                      <Progress
                        percent={score}
                        size="small"
                        strokeColor={score >= 80 ? '#52c41a' : score >= 50 ? '#1890ff' : '#ff4d4f'}
                        format={(p) => `${p}`}
                      />
                    </div>
                  );
                })}
              </div>

              {tool.compliance_updated_at && (
                <div style={{ marginTop: 12, textAlign: 'right' }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {t('detail.updatedAt')} {dayjs(tool.compliance_updated_at).fromNow()}
                  </Text>
                </div>
              )}
            </Card>
          )}

          {/* 企业采购信息 */}
          {(tool.supports_invoice || tool.supports_contract || tool.supports_on_prem) && (
            <Card style={{ borderRadius: 12, marginBottom: 16 }}>
              <Title level={5}>{t('detail.enterpriseProcurement')}</Title>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {tool.supports_invoice && <Tag color="cyan" icon={<CheckCircleOutlined />}>{t('detail.supportsInvoice')}</Tag>}
                {tool.supports_contract && <Tag color="cyan" icon={<CheckCircleOutlined />}>{t('detail.supportsContract')}</Tag>}
                {tool.supports_on_prem && <Tag color="cyan" icon={<CheckCircleOutlined />}>{t('detail.supportsOnPrem')}</Tag>}
                {tool.procurement_channel && tool.procurement_channel !== 'direct' && (
                  <div>
                    <Text type="secondary">{t('detail.procurementChannel')}: </Text>
                    <Text>{tool.procurement_channel === 'reseller' ? t('detail.channelReseller') : tool.procurement_channel === 'cloud_marketplace' ? t('detail.channelMarketplace') : t('detail.channelOpenSource')}</Text>
                  </div>
                )}
                {tool.procurement_contact && (
                  <div>
                    <Text type="secondary">{t('detail.procurementContact')}: </Text>
                    <a href={tool.procurement_contact} target="_blank" rel="noopener noreferrer">{tool.procurement_contact}</a>
                  </div>
                )}
                {tool.enterprise_features && (() => {
                  try {
                    const features: string[] = JSON.parse(tool.enterprise_features);
                    return features.length > 0 && (
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary">{t('detail.enterpriseFeatures')}: </Text>
                        <div style={{ marginTop: 4 }}>
                          {features.map((f, i) => <Tag key={i}>{f}</Tag>)}
                        </div>
                      </div>
                    );
                  } catch { return null; }
                })()}
              </div>
            </Card>
          )}

          {tool.api_doc_url && (
            <Card style={{ borderRadius: 12 }}>
              <Title level={5}>{t('detail.devResources')}</Title>
              <Button
                type="primary"
                ghost
                block
                icon={<FileTextOutlined />}
                href={tool.api_doc_url}
                target="_blank"
              >
                {t('detail.apiDoc')}
              </Button>
            </Card>
          )}
        </Col>
      </Row>

      <AdSlot placement="detail" />
    </div>
  );
};

export default ToolDetailPage;
